/**
 * Vault page -- the main interface for the patient's encrypted health vault.
 *
 * States:
 * - Not initialized -> redirect to /vault/setup
 * - Locked          -> show unlock screen (biometric + password fallback)
 * - Unlocked        -> record list, add record, lock, backup
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePatientVault, VaultRecordMeta, HealthRecord } from '@/hooks/use-patient-vault';
import {
  Lock,
  Unlock,
  Shield,
  Fingerprint,
  Key,
  Plus,
  Trash2,
  Download,
  Upload,
  Eye,
  FileText,
  AlertTriangle,
  Copy,
  CheckCircle,
  RefreshCw,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Unlock screen shown when vault is locked */
const UnlockScreen: React.FC<{
  vault: ReturnType<typeof usePatientVault>;
}> = ({ vault }) => {
  const [seedInput, setSeedInput] = useState('');
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [method, setMethod] = useState<'biometric' | 'password'>(
    vault.biometric.isSupported && vault.biometric.isRegistered ? 'biometric' : 'password',
  );

  const handleUnlock = useCallback(async () => {
    setIsUnlocking(true);
    try {
      if (method === 'biometric') {
        await vault.unlockVault('biometric');
      } else {
        await vault.unlockVault('password', seedInput);
      }
    } finally {
      setIsUnlocking(false);
    }
  }, [method, seedInput, vault]);

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 p-4 rounded-full bg-muted/50">
            <Lock className="h-10 w-10 text-primary" />
          </div>
          <CardTitle>Vault Locked</CardTitle>
          <CardDescription>
            Authenticate to access your encrypted health records.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {vault.error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm">
              {vault.error}
            </div>
          )}

          {/* Biometric unlock */}
          {vault.biometric.isSupported && vault.biometric.isRegistered && (
            <div className="space-y-3">
              {method === 'biometric' && (
                <Button
                  className="w-full h-14"
                  onClick={handleUnlock}
                  disabled={isUnlocking}
                >
                  <Fingerprint className="mr-2 h-5 w-5" />
                  {isUnlocking ? 'Verifying...' : 'Unlock with Biometric'}
                </Button>
              )}
            </div>
          )}

          {/* Password / seed phrase fallback */}
          {method === 'password' && (
            <div className="space-y-3">
              <label
                htmlFor="seed-input"
                className="block text-sm font-medium text-foreground"
              >
                Recovery Phrase
              </label>
              <textarea
                id="seed-input"
                rows={3}
                placeholder="Enter your 12-word recovery phrase..."
                value={seedInput}
                onChange={(e) => setSeedInput(e.target.value)}
                className={cn(
                  'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                  'placeholder:text-muted-foreground resize-none',
                )}
              />
              <Button
                className="w-full"
                onClick={handleUnlock}
                disabled={isUnlocking || !seedInput.trim()}
              >
                <Key className="mr-2 h-4 w-4" />
                {isUnlocking ? 'Unlocking...' : 'Unlock with Recovery Phrase'}
              </Button>
            </div>
          )}

          {/* Toggle method */}
          {vault.biometric.isSupported && vault.biometric.isRegistered && (
            <button
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              onClick={() =>
                setMethod((m) => (m === 'biometric' ? 'password' : 'biometric'))
              }
            >
              {method === 'biometric'
                ? 'Use recovery phrase instead'
                : 'Use biometric instead'}
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/** Modal for adding a new record */
const AddRecordModal: React.FC<{
  onAdd: (record: HealthRecord) => Promise<void>;
  onClose: () => void;
}> = ({ onAdd, onClose }) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('general');
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const RECORD_TYPES = [
    { value: 'general', label: 'General' },
    { value: 'lab-result', label: 'Lab Result' },
    { value: 'prescription', label: 'Prescription' },
    { value: 'imaging', label: 'Imaging' },
    { value: 'consultation', label: 'Consultation Note' },
    { value: 'immunization', label: 'Immunization' },
    { value: 'insurance', label: 'Insurance Document' },
  ];

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!title.trim()) return;
      setIsSaving(true);
      try {
        const record: HealthRecord = {
          id: `rec_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          type,
          title: title.trim(),
          data: content.trim() || null,
          createdAt: new Date().toISOString(),
        };
        await onAdd(record);
        onClose();
      } catch {
        // Error handled by parent
      } finally {
        setIsSaving(false);
      }
    },
    [title, type, content, onAdd, onClose],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-lg mx-4">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Add Health Record
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardDescription>
            This record will be encrypted and stored locally on your device.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="record-title"
                className="block text-sm font-medium mb-1"
              >
                Title
              </label>
              <input
                id="record-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Blood Test Results - March 2026"
                className={cn(
                  'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                  'placeholder:text-muted-foreground',
                )}
                required
              />
            </div>

            <div>
              <label
                htmlFor="record-type"
                className="block text-sm font-medium mb-1"
              >
                Record Type
              </label>
              <select
                id="record-type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className={cn(
                  'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                )}
              >
                {RECORD_TYPES.map((rt) => (
                  <option key={rt.value} value={rt.value}>
                    {rt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="record-content"
                className="block text-sm font-medium mb-1"
              >
                Content / Notes
              </label>
              <textarea
                id="record-content"
                rows={4}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter any notes, values, or details..."
                className={cn(
                  'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                  'placeholder:text-muted-foreground resize-none',
                )}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving || !title.trim()}>
                <Shield className="mr-2 h-4 w-4" />
                {isSaving ? 'Encrypting...' : 'Encrypt & Store'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

/** Backup / restore section */
const BackupSection: React.FC<{
  vault: ReturnType<typeof usePatientVault>;
}> = ({ vault }) => {
  const [backupPassword, setBackupPassword] = useState('');
  const [backupString, setBackupString] = useState('');
  const [restoreKey, setRestoreKey] = useState('');
  const [restorePassword, setRestorePassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showRestore, setShowRestore] = useState(false);
  const [backupError, setBackupError] = useState<string | null>(null);

  const handleExport = useCallback(async () => {
    if (backupPassword.length < 8) {
      setBackupError('Password must be at least 8 characters.');
      return;
    }
    setBackupError(null);
    setIsExporting(true);
    try {
      const result = await vault.exportBackup(backupPassword);
      setBackupString(result);
    } catch (err) {
      setBackupError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  }, [backupPassword, vault]);

  const handleCopyBackup = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(backupString);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Clipboard not available
    }
  }, [backupString]);

  const handleImport = useCallback(async () => {
    setBackupError(null);
    setIsImporting(true);
    try {
      await vault.importBackup(restoreKey, restorePassword);
      setShowRestore(false);
      setRestoreKey('');
      setRestorePassword('');
    } catch (err) {
      setBackupError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setIsImporting(false);
    }
  }, [restoreKey, restorePassword, vault]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Download className="h-4 w-4 text-primary" />
          Backup & Restore
        </CardTitle>
        <CardDescription>
          Export your encryption key as a password-protected backup.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {backupError && (
          <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm">
            {backupError}
          </div>
        )}

        {/* Export */}
        <div className="space-y-2">
          <label htmlFor="backup-pw" className="block text-sm font-medium">
            Backup Password
          </label>
          <div className="flex gap-2">
            <input
              id="backup-pw"
              type="password"
              value={backupPassword}
              onChange={(e) => setBackupPassword(e.target.value)}
              placeholder="Min. 8 characters"
              className={cn(
                'flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                'placeholder:text-muted-foreground',
              )}
            />
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={isExporting || backupPassword.length < 8}
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </div>

        {backupString && (
          <div className="space-y-2">
            <label className="block text-sm font-medium">Encrypted Key Backup</label>
            <div className="p-3 rounded-lg bg-muted/30 border text-xs font-mono break-all max-h-24 overflow-y-auto">
              {backupString}
            </div>
            <Button variant="outline" size="sm" onClick={handleCopyBackup}>
              <Copy className="mr-2 h-3 w-3" />
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        )}

        {/* Restore toggle */}
        <div className="border-t pt-4">
          <button
            className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            onClick={() => setShowRestore((v) => !v)}
          >
            <Upload className="h-3 w-3" />
            {showRestore ? 'Hide restore options' : 'Restore from backup'}
          </button>
        </div>

        {showRestore && (
          <div className="space-y-3 p-4 rounded-lg border bg-muted/20">
            <div>
              <label htmlFor="restore-key" className="block text-sm font-medium mb-1">
                Backup String
              </label>
              <textarea
                id="restore-key"
                rows={2}
                value={restoreKey}
                onChange={(e) => setRestoreKey(e.target.value)}
                placeholder="Paste your encrypted key backup..."
                className={cn(
                  'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                  'placeholder:text-muted-foreground resize-none font-mono',
                )}
              />
            </div>
            <div>
              <label htmlFor="restore-pw" className="block text-sm font-medium mb-1">
                Backup Password
              </label>
              <input
                id="restore-pw"
                type="password"
                value={restorePassword}
                onChange={(e) => setRestorePassword(e.target.value)}
                placeholder="Enter the password used during export"
                className={cn(
                  'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                  'placeholder:text-muted-foreground',
                )}
              />
            </div>
            <Button
              onClick={handleImport}
              disabled={isImporting || !restoreKey.trim() || !restorePassword}
            >
              <Upload className="mr-2 h-4 w-4" />
              {isImporting ? 'Restoring...' : 'Restore Key'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Main Vault page
// ---------------------------------------------------------------------------
const Vault: React.FC = () => {
  const navigate = useNavigate();
  const vault = usePatientVault();

  const [records, setRecords] = useState<VaultRecordMeta[]>([]);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState<{
    id: string;
    data: unknown;
  } | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  // Redirect to setup if not initialized
  useEffect(() => {
    if (!vault.isLoading && !vault.isInitialized) {
      navigate('/vault/setup', { replace: true });
    }
  }, [vault.isLoading, vault.isInitialized, navigate]);

  // Load record list when unlocked
  const loadRecords = useCallback(async () => {
    setIsLoadingRecords(true);
    try {
      const list = await vault.listRecords();
      setRecords(list.sort((a, b) => b.storedAt.localeCompare(a.storedAt)));
    } catch {
      // Error handled via vault.error
    } finally {
      setIsLoadingRecords(false);
    }
  }, [vault]);

  useEffect(() => {
    if (vault.isUnlocked) {
      loadRecords();
    }
  }, [vault.isUnlocked, loadRecords]);

  // Add record handler
  const handleAddRecord = useCallback(
    async (record: HealthRecord) => {
      await vault.storeHealthRecord(record);
      await loadRecords();
    },
    [vault, loadRecords],
  );

  // Decrypt and view a record
  const handleViewRecord = useCallback(
    async (id: string) => {
      try {
        const data = await vault.getHealthRecord(id);
        setViewingRecord({ id, data });
      } catch {
        // Decryption failure
      }
    },
    [vault],
  );

  // Delete a record
  const handleDeleteRecord = useCallback(
    async (id: string) => {
      setIsDeletingId(id);
      try {
        await vault.deleteHealthRecord(id);
        setRecords((prev) => prev.filter((r) => r.id !== id));
        if (viewingRecord?.id === id) {
          setViewingRecord(null);
        }
      } finally {
        setIsDeletingId(null);
      }
    },
    [vault, viewingRecord],
  );

  // Format date
  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  // Record type badge color
  const typeColor = (type: string) => {
    const map: Record<string, string> = {
      'lab-result': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      prescription: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      imaging: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      consultation: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      immunization: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
      insurance: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
      general: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    };
    return map[type] || map.general;
  };

  // Loading state
  if (vault.isLoading) {
    return (
      <PageContainer title="Health Vault" icon={<Shield />}>
        <div className="flex items-center justify-center min-h-[40vh]">
          <RefreshCw className="h-6 w-6 text-muted-foreground animate-spin" />
        </div>
      </PageContainer>
    );
  }

  // Locked state
  if (!vault.isUnlocked) {
    return (
      <PageContainer title="Health Vault" icon={<Shield />}>
        <UnlockScreen vault={vault} />
      </PageContainer>
    );
  }

  // Unlocked state
  return (
    <PageContainer
      title="Health Vault"
      description="Your encrypted health records, stored securely on this device."
      icon={<Shield />}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => vault.lockVault()}>
            <Lock className="mr-2 h-4 w-4" />
            Lock Vault
          </Button>
        </div>
      }
    >
      {/* Status bar */}
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400">
          <Unlock className="h-4 w-4" />
          <span className="font-medium">Vault Unlocked</span>
        </div>
        <div className="h-4 w-px bg-border" />
        <div className="text-sm text-muted-foreground">
          {records.length} record{records.length !== 1 ? 's' : ''} stored
        </div>
        <div className="h-4 w-px bg-border" />
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Shield className="h-3.5 w-3.5" />
          AES-GCM-256 encrypted
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Records list */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Stored Records</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadRecords} disabled={isLoadingRecords}>
                <RefreshCw className={cn('mr-2 h-3.5 w-3.5', isLoadingRecords && 'animate-spin')} />
                Refresh
              </Button>
              <Button size="sm" onClick={() => setShowAddModal(true)}>
                <Plus className="mr-2 h-3.5 w-3.5" />
                Add Record
              </Button>
            </div>
          </div>

          {records.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground">No records stored yet.</p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  Add your first health record to start building your vault.
                </p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setShowAddModal(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add First Record
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {records.map((record) => (
                <Card
                  key={record.id}
                  className={cn(
                    'transition-all hover:shadow-md cursor-pointer',
                    viewingRecord?.id === record.id && 'ring-2 ring-primary',
                  )}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div
                        className="flex-1 min-w-0"
                        onClick={() => handleViewRecord(record.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleViewRecord(record.id);
                          }
                        }}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                              typeColor(record.type),
                            )}
                          >
                            {record.type}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(record.storedAt)}
                          </span>
                        </div>
                        <p className="text-sm font-medium truncate">{record.id}</p>
                      </div>

                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleViewRecord(record.id)}
                          title="Decrypt and view"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteRecord(record.id)}
                          disabled={isDeletingId === record.id}
                          title="Delete record"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Decrypted record viewer */}
          {viewingRecord && (
            <Card className="border-primary/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                    Decrypted Record
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setViewingRecord(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <CardDescription className="text-xs font-mono">
                  {viewingRecord.id}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="p-3 rounded-lg bg-muted/30 border text-xs overflow-auto max-h-64 whitespace-pre-wrap font-mono">
                  {JSON.stringify(viewingRecord.data, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Vault info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Shield className="h-4 w-4 text-primary" />
                Vault Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Encryption</span>
                <span className="font-medium">AES-GCM-256</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Key Derivation</span>
                <span className="font-medium">PBKDF2 (600k)</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Biometric</span>
                <span className="font-medium">
                  {vault.biometric.isRegistered
                    ? 'Enabled'
                    : vault.biometric.isSupported
                      ? 'Available'
                      : 'Not supported'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Records</span>
                <span className="font-medium">{records.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Storage</span>
                <span className="font-medium">On-device (IndexedDB)</span>
              </div>
            </CardContent>
          </Card>

          {/* Security notice */}
          <Card className="border-amber-200 dark:border-amber-800">
            <CardContent className="p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800 dark:text-amber-200 space-y-1">
                  <p className="font-medium">Security Notice</p>
                  <p>
                    Records are encrypted on this device only. Clearing browser
                    data will delete your vault. Always maintain a backup of your
                    encryption key.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Backup section */}
          <BackupSection vault={vault} />
        </div>
      </div>

      {/* Add record modal */}
      {showAddModal && (
        <AddRecordModal
          onAdd={handleAddRecord}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </PageContainer>
  );
};

export default Vault;
